import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { deleteAccount } from '@/lib/authApi';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';
import { navigateToSignedOutSignIn } from '@/lib/authNavigation';

interface DeleteAccountModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function DeleteAccountModal({ open, onOpenChange }: DeleteAccountModalProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { logout } = useAuth();
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleCancel = () => {
        if (isDeleting) return;
        setError(null);
        onOpenChange(false);
    };

    const handleConfirm = async () => {
        setIsDeleting(true);
        setError(null);

        try {
            const result = await deleteAccount();

            if (result?.success) {
                // Backend already cleared the cookie; clear client auth state and redirect.
                await logout();
                navigateToSignedOutSignIn(navigate);
            } else {
                setError(
                    t('settings.deleteAccount.errorGeneric', {
                        defaultValue: 'Account deletion failed. Please try again.',
                    }),
                );
            }
        } catch (err) {
            logger.error('Account deletion error', err, { event: 'settings.delete_account.failed' });
            setError(
                t('settings.deleteAccount.errorGeneric', {
                    defaultValue: 'Account deletion failed. Please try again.',
                }),
            );
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!isDeleting) onOpenChange(next);
            }}
        >
            <DialogContent className="max-w-md" aria-describedby="delete-account-description">
                <DialogHeader>
                    <DialogTitle className="text-destructive">
                        {t('settings.deleteAccount.title', { defaultValue: 'Delete Account' })}
                    </DialogTitle>
                    <DialogDescription id="delete-account-description">
                        {t('settings.deleteAccount.warning', {
                            defaultValue:
                                'This action is permanent and cannot be undone. Your account will be immediately and irreversibly deleted.',
                        })}
                    </DialogDescription>
                </DialogHeader>

                <div className="py-2 space-y-3">
                    <p className="text-sm font-medium">
                        {t('settings.deleteAccount.willRemove', {
                            defaultValue: 'The following will be permanently removed:',
                        })}
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                        <li>{t('settings.deleteAccount.itemAccount', { defaultValue: 'Your account and profile data' })}</li>
                        <li>{t('settings.deleteAccount.itemSimulations', { defaultValue: 'All simulation history and results' })}</li>
                        <li>{t('settings.deleteAccount.itemAudio', { defaultValue: 'All recorded audio files' })}</li>
                    </ul>

                    {error && (
                        <p
                            role="alert"
                            className="text-sm text-destructive border border-destructive/30 rounded-md px-3 py-2 bg-destructive/5"
                        >
                            {error}
                        </p>
                    )}
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={isDeleting}
                    >
                        {t('common.cancel', { defaultValue: 'Cancel' })}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleConfirm}
                        disabled={isDeleting}
                    >
                        {isDeleting
                            ? t('common.processing', { defaultValue: 'Processing...' })
                            : t('settings.deleteAccount.confirm', { defaultValue: 'Delete My Account' })}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
